import { useMemo, useState } from "react";
import { Toaster, toast } from "sonner";
import { Star } from "lucide-react";

// Country flag emoji for a small "location" badge next to reviewer name.
const COUNTRIES = [
  { code: "US", label: "United States", flag: "🇺🇸" },
  { code: "CA", label: "Canada", flag: "🇨🇦" },
  { code: "MX", label: "Mexico", flag: "🇲🇽" },
  { code: "GB", label: "United Kingdom", flag: "🇬🇧" },
  { code: "DE", label: "Germany", flag: "🇩🇪" },
  { code: "IT", label: "Italy", flag: "🇮🇹" },
  { code: "FR", label: "France", flag: "🇫🇷" },
  { code: "AU", label: "Australia", flag: "🇦🇺" },
  { code: "AE", label: "UAE", flag: "🇦🇪" },
  { code: "PK", label: "Pakistan", flag: "🇵🇰" },
];

// Seeded US-based reviews.
const SEED_REVIEWS = [
  {
    id: "r1",
    name: "Marcus D.",
    country: "US",
    rating: 5,
    title: "Feels like a jacket that will outlive me.",
    body:
      "Ordered the Refined Biker in size L. The leather is unbelievably soft on day one and has real weight without being heavy. Stitching is flawless — you can see this is hand-finished.",
    created_at: "2026-01-14",
    verified: true,
  },
  {
    id: "r2",
    name: "Jordan P.",
    country: "US",
    rating: 5,
    title: "Fit guide was spot on.",
    body:
      "Used the Find My Size wizard — got recommended M and it fits like it was cut for me. Delivery was 5 business days to Brooklyn, no fees, insured. Impressed.",
    created_at: "2026-01-22",
    verified: true,
  },
  {
    id: "r3",
    name: "Ethan R.",
    country: "US",
    rating: 4,
    title: "Beautiful piece — collar took a week to soften.",
    body:
      "This is the real deal. Wanted 5 stars but the sheepskin collar was a touch stiff at first. After ~10 wears it moulds perfectly. Would buy from Boscage again.",
    created_at: "2026-02-03",
    verified: true,
  },
  {
    id: "r4",
    name: "Chris W.",
    country: "US",
    rating: 5,
    title: "The trade-in program sold me.",
    body:
      "Sent in my 8-year-old café racer, got a fair valuation towards the Suede Harrington. Zero pressure, honest team. This is how leather should be bought.",
    created_at: "2026-02-11",
    verified: true,
  },
];

export default function ReviewsPage() {
  const [reviews, setReviews] = useState(SEED_REVIEWS);
  const [name, setName] = useState("");
  const [country, setCountry] = useState("US");
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const avg = useMemo(() => {
    if (!reviews.length) return 0;
    return reviews.reduce((s, r) => s + r.rating, 0) / reviews.length;
  }, [reviews]);

  const submit = (e) => {
    e.preventDefault();
    if (!name || !title || !body) return;
    setSubmitting(true);
    // Local-only for now — will POST /api/reviews once endpoint ships.
    const review = {
      id: `r_${Date.now()}`,
      name,
      country,
      rating,
      title,
      body,
      created_at: new Date().toISOString().slice(0, 10),
      verified: false,
    };
    setReviews((r) => [review, ...r]);
    setName("");
    setTitle("");
    setBody("");
    setRating(5);
    toast.success("Thank you — your review has been posted.");
    setSubmitting(false);
  };

  return (
    <div data-testid="reviews-page" className="bg-white min-h-screen">
      {/* Header */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-10 border-b border-black/10">
        <p className="text-[10px] tracking-[0.32em] uppercase text-[#c9a84c]">
          Customer voices
        </p>
        <h1 className="mt-3 font-serif text-4xl md:text-6xl leading-tight text-black">
          Reviews from those who wear Boscage.
        </h1>
        <div className="mt-8 flex flex-wrap items-baseline gap-x-8 gap-y-3">
          <div className="flex items-center gap-2" data-testid="reviews-average">
            <div className="flex">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star
                  key={i}
                  size={20}
                  strokeWidth={1.3}
                  className={i <= Math.round(avg) ? "fill-[#c9a84c] text-[#c9a84c]" : "text-black/25"}
                />
              ))}
            </div>
            <span className="font-serif text-2xl text-black ml-2">{avg.toFixed(1)}</span>
            <span className="text-[11px] tracking-[0.18em] uppercase text-black/55">
              / 5 · {reviews.length} reviews
            </span>
          </div>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 grid lg:grid-cols-[2fr_1fr] gap-14">
        {/* Reviews list */}
        <div>
          <h2 className="font-serif text-2xl text-black mb-8">What owners say</h2>
          <ul className="space-y-8" data-testid="reviews-list">
            {reviews.map((r) => (
              <li
                key={r.id}
                data-testid={`review-item-${r.id}`}
                className="border-b border-black/10 pb-8"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#0a0a0a] text-white flex items-center justify-center font-serif text-sm">
                    {r.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-[14px] text-black leading-tight flex items-center gap-2">
                      {r.name}
                      <span
                        data-testid={`review-flag-${r.id}`}
                        title={COUNTRIES.find((c) => c.code === r.country)?.label}
                        className="text-[14px]"
                        aria-label={COUNTRIES.find((c) => c.code === r.country)?.label}
                      >
                        {COUNTRIES.find((c) => c.code === r.country)?.flag || "🌐"}
                      </span>
                      {r.verified && (
                        <span className="text-[9px] tracking-[0.22em] uppercase text-[#c9a84c] border border-[#c9a84c] px-1.5 py-[1px]">
                          Verified
                        </span>
                      )}
                    </p>
                    <p className="text-[11px] tracking-[0.14em] uppercase text-black/45 mt-0.5">
                      {r.created_at}
                    </p>
                  </div>
                </div>
                <div className="mt-3 flex">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star
                      key={i}
                      size={14}
                      strokeWidth={1.3}
                      className={i <= r.rating ? "fill-[#c9a84c] text-[#c9a84c]" : "text-black/25"}
                    />
                  ))}
                </div>
                <h3 className="mt-3 font-serif text-lg text-black">{r.title}</h3>
                <p className="mt-2 text-[14px] leading-relaxed text-black/75">{r.body}</p>
              </li>
            ))}
          </ul>
        </div>

        {/* Form */}
        <aside className="lg:sticky lg:top-24 self-start bg-[#fafafa] border border-black/10 p-6 md:p-8" data-testid="review-form-card">
          <p className="text-[10px] tracking-[0.28em] uppercase text-[#c9a84c]">Share your voice</p>
          <h2 className="mt-2 font-serif text-2xl text-black">Write a review</h2>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="review-form">
            <div>
              <p className="text-[11px] tracking-[0.18em] uppercase text-black/70 mb-2">Rating</p>
              <div className="flex gap-1" data-testid="review-rating">
                {[1, 2, 3, 4, 5].map((i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setRating(i)}
                    data-testid={`review-star-${i}`}
                    aria-label={`${i} star${i > 1 ? "s" : ""}`}
                  >
                    <Star
                      size={26}
                      strokeWidth={1.4}
                      className={i <= rating ? "fill-[#c9a84c] text-[#c9a84c]" : "text-black/25 hover:text-[#c9a84c]"}
                    />
                  </button>
                ))}
              </div>
            </div>

            <input data-testid="review-name" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-black/20 px-4 py-3 text-sm focus:outline-none focus:border-black" />

            <select data-testid="review-country" value={country} onChange={(e) => setCountry(e.target.value)} className="w-full border border-black/20 px-4 py-3 text-sm focus:outline-none focus:border-black bg-white">
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>{c.flag} &nbsp; {c.label}</option>
              ))}
            </select>

            <input data-testid="review-title" placeholder="Headline (e.g. Feels premium day one)" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full border border-black/20 px-4 py-3 text-sm focus:outline-none focus:border-black" />

            <textarea data-testid="review-body" placeholder="Tell us about your Boscage piece — fit, feel, service." value={body} onChange={(e) => setBody(e.target.value)} className="w-full border border-black/20 px-4 py-3 text-sm focus:outline-none focus:border-black min-h-[140px]" />

            <button type="submit" disabled={submitting} data-testid="review-submit" className="w-full bg-black text-white py-3.5 text-[12px] tracking-[0.22em] uppercase hover:bg-[#c9a84c] hover:text-black transition-colors disabled:opacity-40">
              {submitting ? "Posting…" : "Post review"}
            </button>
          </form>
        </aside>
      </div>
      <Toaster theme="dark" position="top-center" />
    </div>
  );
}
